import React, { PureComponent, createRef } from "react";
import PropTypes from "prop-types";
import { Editor, EditorState, RichUtils, convertToRaw, convertFromRaw, Modifier } from "draft-js";
import i18n from "i18next";

import StyleButton from "./StyleButton";

const inlineStyles = ["BOLD", "ITALIC", "UNDERLINE"];
const colorStyles = ["RED", "GREEN", "BLUE", "YELLOW"];

const styleMap = {
  RED: { color: "#ff4646" },
  YELLOW: { color: "#ffc419" },
  GREEN: { color: "#16c48c" },
  BLUE: { color: "#5b9fc0" }
};

export default class RichEditor extends PureComponent {
  static propTypes = {
    onChange: PropTypes.func.isRequired,
    initialValue: PropTypes.string,
    placeholder: PropTypes.string
  };

  constructor(props) {
    super(props);
    this.state = { editorState: this.getInitialEditorState() };
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.initialValue && this.props.initialValue) this.reinitializeEditor();
  }

  getInitialEditorState = () =>
    this.props.initialValue ? EditorState.createWithContent(convertFromRaw(JSON.parse(this.props.initialValue))) : EditorState.createEmpty();

  editorRef = createRef();

  reinitializeEditor = () => this.setState({ editorState: this.getInitialEditorState() });

  handleChange = editorState => {
    this.setState({ editorState }, () => {
      const hasText = this.state.editorState.getCurrentContent().hasText();
      const value = hasText ? JSON.stringify(convertToRaw(this.state.editorState.getCurrentContent())) : "";
      this.props.onChange(value);
    });
  };

  focus = () => this.editorRef.current.focus();

  toggleInlineStyle = inlineStyle => this.handleChange(RichUtils.toggleInlineStyle(this.state.editorState, inlineStyle));

  toggleColorStyle = toggledColor => {
    const selection = this.state.editorState.getSelection();

    // Let's just allow one color at a time. Turn off all active colors.
    const nextContentState = Object.keys(styleMap).reduce(
      (contentState, color) => Modifier.removeInlineStyle(contentState, selection, color),
      this.state.editorState.getCurrentContent()
    );

    let nextEditorState = EditorState.push(this.state.editorState, nextContentState, "change-inline-style");

    const currentStyle = this.state.editorState.getCurrentInlineStyle();

    // Unset style override for current color.
    if (selection.isCollapsed()) {
      nextEditorState = currentStyle.reduce((state, color) => RichUtils.toggleInlineStyle(state, color), nextEditorState);
    }

    if (!currentStyle.has(toggledColor)) {
      nextEditorState = RichUtils.toggleInlineStyle(nextEditorState, toggledColor);
    }

    this.handleChange(nextEditorState);
  };

  render() {
    const currentStyle = this.state.editorState.getCurrentInlineStyle();

    return (
      <div className="rich-editor">
        <div className="rich-editor__controls">
          {inlineStyles.map(style => (
            <StyleButton
              key={style}
              isActive={currentStyle.has(style)}
              label={i18n.t(`richEditor.${style}`)}
              onToggle={this.toggleInlineStyle}
              style={style}
            />
          ))}
        </div>

        <div className="rich-editor__controls">
          {colorStyles.map(style => (
            <StyleButton
              key={style}
              isActive={currentStyle.has(style)}
              label={i18n.t(`richEditor.${style}`)}
              onToggle={this.toggleColorStyle}
              style={style}
            />
          ))}
        </div>

        <div className="rich-editor__editor" onClick={this.focus} role="none">
          <Editor
            editorState={this.state.editorState}
            customStyleMap={styleMap}
            onChange={this.handleChange}
            placeholder={this.props.placeholder}
            ref={this.editorRef}
          />
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/Common/RichEditor/RichEditor.js